/**
 * On-hold Orders Hooks
 *
 * TanStack Query hooks for the "On hold" tab: list ON_HOLD orders for a location
 * and send an order back into production.
 */
import { trpc } from '@/lib/trpc';

/**
 * Hook to fetch ON_HOLD orders for a location (operator holds + Shopify holds).
 */
export function useOnHoldOrders(locationId: string) {
  const query = trpc.productionOrders.issues.getOnHoldOrders.useQuery(
    { location_id: locationId },
    {
      enabled: !!locationId,
      refetchInterval: 30_000, // Refresh every 30 seconds
    }
  );

  return {
    orders: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to send an ON_HOLD order back into production.
 */
export function useSendBackToProduction() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.sendBackToProduction.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.getOnHoldOrders.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
      // A released order REAPPEARS on the Tracker (the Orders tab's RPC excludes
      // ON_HOLD, the queue's list is NEW-only), so both surfaces must refetch —
      // otherwise it stays invisible until a manual refresh. Mirrors the
      // invalidation set the hold direction uses.
      utils.productionOrders.listTrackerOrders.invalidate();
      utils.productionOrders.getTrackerCounts.invalidate();
      utils.productionOrders.getOrderQueue.invalidate();
      utils.productionOrders.getQueueCounts.invalidate();
    },
  });
}

/**
 * Hook to mark an on-hold order's customer contacted / not contacted
 * (the On-Hold tab's clickable "customer contacted" badge).
 */
export function useSetCustomerContacted() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.issues.setCustomerContacted.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.getOnHoldOrders.invalidate();
    },
  });
}
